/**
 * usePrintInvoices Hook (BLU-328)
 *
 * Handles printing order invoices/packing slips via browser print dialog.
 * Mirrors usePrintLabels pattern but uses A4 page size and `.invoice` class.
 *
 * CRITICAL: Invoice components must use inline styles only.
 * The printHtmlViaIframe function copies innerHTML into an iframe
 * that has no access to the app's Tailwind CSS.
 */

import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { printHtmlViaIframe } from '@/lib/print-utils';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface UsePrintInvoicesOptions {
  onPrintStart?: () => void;
  onPrintComplete?: () => void;
  onPrintError?: (error: Error) => void;
}

interface UsePrintInvoicesReturn {
  /** Ref to attach to hidden print container */
  printRef: React.RefObject<HTMLDivElement>;
  /** Trigger print operation */
  printInvoices: (documentTitle?: string) => void;
  /** Whether print is in progress */
  isPrinting: boolean;
}

export function usePrintInvoices(
  options: UsePrintInvoicesOptions = {}
): UsePrintInvoicesReturn {
  const { t } = useTranslation('production');
  const printRef = useRef<HTMLDivElement | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const printInvoices = useCallback((documentTitle?: string) => {
    const { onPrintStart, onPrintComplete, onPrintError } = optionsRef.current;

    if (!printRef.current) {
      toast.error(t('invoice.noInvoices'));
      return;
    }

    const invoiceCount = printRef.current.querySelectorAll('.invoice').length;
    if (invoiceCount === 0) {
      toast.error(t('invoice.noInvoicesFound'));
      return;
    }

    setIsPrinting(true);
    onPrintStart?.();

    try {
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta name="color-scheme" content="light only">
            <title>${escapeHtml(documentTitle || 'Order Invoices')}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              img { image-rendering: auto; }
              body {
                font-family: Arial, Helvetica, "PingFang SC", "Microsoft YaHei", sans-serif;
                color: #111;
                background: #fff;
                font-size: 11pt;
              }

              @page {
                size: A4 portrait;
                margin: 15mm;
              }

              @media print {
                .invoice {
                  page-break-inside: avoid;
                }
                .invoice + .invoice {
                  page-break-before: always;
                }
              }
            </style>
          </head>
          <body>
            ${printRef.current.innerHTML}
          </body>
        </html>
      `;

      printHtmlViaIframe(printHTML, () => {
        setIsPrinting(false);
        onPrintComplete?.();
      });

      toast.success(t('invoice.sentToPrinter', { count: invoiceCount }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown print error');
      toast.error(t('invoice.printFailed'), {
        description: error.message,
      });
      onPrintError?.(error);
      setIsPrinting(false);
    }
  }, []);

  return { printRef, printInvoices, isPrinting };
}
